/**
 * The companion character: a sports ball with a face.
 *
 * Renders as inline SVG so the face can be animated by CSS/JS without any
 * image assets. Every companion in the roster reuses this one renderer -
 * only colors and seam geometry change.
 */

import { byId } from '../shared/companions.js';

const MOOD_EYES = {
  happy:     { ry: 9,  tilt: 0,  browY: -2, browTilt: 0 },
  hype:      { ry: 11, tilt: 0,  browY: -5, browTilt: -8 },
  thinking:  { ry: 6,  tilt: -4, browY: -1, browTilt: 12 },
  concerned: { ry: 7,  tilt: 3,  browY: 2,  browTilt: 16 },
  proud:     { ry: 5,  tilt: 0,  browY: -4, browTilt: -4 },
};

const MOOD_MOUTH = {
  happy:     'M36 66 Q50 78 64 66',
  hype:      'M34 63 Q50 84 66 63 Q50 72 34 63',
  thinking:  'M40 70 Q50 66 60 70',
  concerned: 'M38 74 Q50 64 62 74',
  proud:     'M37 67 Q50 76 63 67',
};

/**
 * @param {string} companionId
 * @param {object} opts  { size, mood, idle }
 * @returns {string} SVG markup
 */
export function companionSVG(companionId, opts = {}) {
  const c = byId(companionId);
  const { size = 180, mood = 'happy', idle = true } = opts;
  const m = MOOD_EYES[mood] || MOOD_EYES.happy;
  const mouth = MOOD_MOUTH[mood] || MOOD_MOUTH.happy;
  const uid = `${c.id}-${Math.random().toString(36).slice(2, 7)}`;

  // Puck is a squat cylinder, not a sphere - give it its own silhouette.
  const body =
    c.id === 'hockey'
      ? `<ellipse cx="50" cy="56" rx="42" ry="30" fill="url(#g${uid})"/>
         <ellipse cx="50" cy="46" rx="42" ry="30" fill="${c.base}"/>
         <ellipse cx="50" cy="46" rx="42" ry="30" fill="none" stroke="${c.line}" stroke-width="1.5" opacity=".5"/>`
      : c.id === 'football' || c.id === 'rugby'
        ? `<ellipse cx="50" cy="50" rx="34" ry="45" fill="url(#g${uid})"/>`
        : `<circle cx="50" cy="50" r="44" fill="url(#g${uid})"/>`;

  return `
<svg class="companion ${idle ? 'idle' : ''}" data-mood="${mood}" viewBox="0 0 100 100"
     width="${size}" height="${size}" role="img" aria-label="${c.name} the ${c.sport.toLowerCase()}">
  <defs>
    <radialGradient id="g${uid}" cx="36%" cy="30%">
      <stop offset="0%" stop-color="${c.base}"/>
      <stop offset="72%" stop-color="${c.base}"/>
      <stop offset="100%" stop-color="${c.shade}"/>
    </radialGradient>
    <clipPath id="c${uid}">
      ${c.id === 'hockey'
        ? `<ellipse cx="50" cy="46" rx="42" ry="30"/>`
        : c.id === 'football' || c.id === 'rugby'
          ? `<ellipse cx="50" cy="50" rx="34" ry="45"/>`
          : `<circle cx="50" cy="50" r="44"/>`}
    </clipPath>
  </defs>

  <g class="c-body">
    ${body}
    <g clip-path="url(#c${uid})" opacity=".85">
      <path d="${c.seams}" fill="none" stroke="${c.line}" stroke-width="2.4" stroke-linecap="round"/>
    </g>
    <!-- gloss -->
    <ellipse cx="34" cy="28" rx="13" ry="8" fill="#fff" opacity=".22" transform="rotate(-24 34 28)"/>

    <g class="c-face">
      <!-- brows -->
      <g stroke="${c.line}" stroke-width="3" stroke-linecap="round" opacity=".92">
        <line x1="28" y1="${34 + m.browY}" x2="42" y2="${34 + m.browY + m.browTilt * 0.25}"
              transform="rotate(${-m.browTilt} 35 34)"/>
        <line x1="58" y1="${34 + m.browY + m.browTilt * 0.25}" x2="72" y2="${34 + m.browY}"
              transform="rotate(${m.browTilt} 65 34)"/>
      </g>
      <!-- eyes -->
      <g class="c-eyes">
        <ellipse class="eye" cx="36" cy="48" rx="8" ry="${m.ry}" fill="#12161d"
                 transform="rotate(${m.tilt} 36 48)"/>
        <ellipse class="eye" cx="64" cy="48" rx="8" ry="${m.ry}" fill="#12161d"
                 transform="rotate(${-m.tilt} 64 48)"/>
        <circle cx="38.5" cy="44.5" r="2.8" fill="#fff"/>
        <circle cx="66.5" cy="44.5" r="2.8" fill="#fff"/>
      </g>
      <!-- mouth -->
      <path class="c-mouth" d="${mouth}" fill="none" stroke="#12161d"
            stroke-width="3.4" stroke-linecap="round"/>
    </g>
  </g>
</svg>`;
}

/**
 * 3D companion: a rendered character with a living aura behind it. The aura is
 * how the player always knows what the companion is doing:
 *   idle      - slow breathe, soft glow
 *   listening - green ring expanding outward (mic is open)
 *   thinking  - dashed ring rotating with orbiting dots
 *   talking   - fast pulsing aura, character bobs with the voice
 */
export function companion3D(companionId, opts = {}) {
  const c = byId(companionId);
  const { size = 180, state = 'idle', mood = 'happy' } = opts;
  return `
<div class="c3d state-${state}" data-mood="${mood}" style="--size:${size}px;--aura:${c.base};--aura2:${c.shade}"
     role="img" aria-label="${c.name} the ${c.sport.toLowerCase()}">
  <div class="c3d-aura"></div>
  <div class="c3d-ring"></div>
  <div class="c3d-dots"><i></i><i></i><i></i></div>
  <img class="c3d-img" src="${c.image}" alt="" draggable="false">
</div>`;
}

const STATES = ['idle', 'listening', 'thinking', 'talking'];

/** Drop a companion into a container and return a small control handle. */
export function mountCompanion(el, companionId, opts = {}) {
  if (byId(companionId).image) return mount3D(el, companionId, opts);
  el.innerHTML = companionSVG(companionId, opts);
  const svg = el.querySelector('svg');
  let blinkTimer;

  const blink = () => {
    const eyes = svg.querySelector('.c-eyes');
    if (!eyes) return;
    eyes.classList.add('blinking');
    setTimeout(() => eyes.classList.remove('blinking'), 150);
    blinkTimer = setTimeout(blink, 2200 + Math.random() * 3800);
  };
  blinkTimer = setTimeout(blink, 1200);

  return {
    el: svg,
    setMood(mood) {
      el.innerHTML = companionSVG(companionId, { ...opts, mood });
      const next = el.querySelector('svg');
      if (svg.classList.contains('talking')) next.classList.add('talking');
    },
    swap(nextId) {
      companionId = nextId;
      el.innerHTML = companionSVG(companionId, opts);
    },
    talk(on) {
      el.querySelector('svg')?.classList.toggle('talking', on);
    },
    setState(state) { this.talk(state === 'talking'); },
    destroy() { clearTimeout(blinkTimer); },
  };
}

function mount3D(el, companionId, opts) {
  let state = 'idle';
  const render = () => { el.innerHTML = companion3D(companionId, { ...opts, state }); };
  render();
  const root = () => el.querySelector('.c3d');
  const handle = {
    get el() { return root(); },
    setMood(mood) { root()?.setAttribute('data-mood', mood); },
    swap(nextId) {
      companionId = nextId;
      if (!byId(nextId).image) { el.innerHTML = companionSVG(nextId, opts); return; }
      render();
    },
    setState(next) {
      if (!STATES.includes(next)) next = 'idle';
      state = next;
      const r = root(); if (!r) return;
      STATES.forEach((s) => r.classList.toggle(`state-${s}`, s === next));
    },
    talk(on) { handle.setState(on ? 'talking' : 'idle'); },
    destroy() {},
  };
  return handle;
}
